import { ApprovalableTypeEnum } from 'api-hooks/common';
import {
  ActivitySaleModel,
  getActivitySalesKey,
  useCreateActivitySale,
} from 'api-hooks/sale/activity';
import { ApprovalItemWorkflowEnum } from 'api-hooks/sale/common';
import { KIND, Notification } from 'baseui/notification';
import { NavigationRoutes } from 'common/constants';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import { isDataDraft } from 'hooks/use-approval-action';
import useDialog from 'hooks/use-dialog';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import { SeparatorVertical } from 'legacy-modules/styles';
import * as React from 'react';
import { NavigationProps } from 'types';

import ActivitySaleForm from './components/form';
import { ActivitySaleFormType } from './components/form-type';

export type ActivitySaleCreateParams = {
  defaultValues?: ActivitySaleFormType;
  activitySale?: ActivitySaleModel;
};

export default function ActivitySaleCreate(
  props: NavigationProps<NavigationRoutes.ActivitySalesCreate>,
) {
  const { navigate } = useNavigation();

  const { mutateAsync, error } = useCreateActivitySale();
  const dialog = useDialog();

  const defaultValues = props.navigation.params?.defaultValues;
  const permission = useGetCrudPermission('activity-sales');
  const activityPurchasePermission = useGetCrudPermission('activity-purchases');

  return (
    <>
      <ActivitySaleForm
        defaultValues={defaultValues}
        {...props}
        renderError={
          !!error &&
          !!error.message && (
            <>
              <Notification
                overrides={{
                  Body: { style: { width: 'auto', wordBreak: 'break-all' } },
                }}
                kind={KIND.negative}
                closeable
              >
                {() => error.message}
              </Notification>
              <SeparatorVertical $margin={8} />
            </>
          )
        }
        onSubmit={async (input, form) => {
          const result = await mutateAsync(input);

          const onDismiss = () => {
            queryClient.refetchQueries([getActivitySalesKey()[0]]);

            if (activityPurchasePermission.create) {
              navigate(NavigationRoutes.ActivityPurchasesCreate, {
                activitySale: result.data,
              });
            }

            notification.success({ message: result?.message });
          };

          const onDismissCancel = () => {
            queryClient.refetchQueries([getActivitySalesKey()[0]]);

            if (permission.view) {
              navigate(NavigationRoutes.ActivitySalesView, {
                id: result.data.id,
              });
            }
          };

          const isDraft = isDataDraft(result.data.status);

          if (isDraft) {
            dialog.showCustom({
              render: (onClose) => (
                <RequestApprovalModal
                  onClose={onClose}
                  data={{
                    approvalableId: result.data.id,
                    approvalableType: ApprovalableTypeEnum.Sale,
                    workflow: ApprovalItemWorkflowEnum.sale,
                  }}
                  onDismissSuccess={onDismissCancel}
                  onDismissCancel={onDismissCancel}
                />
              ),
            });
          } else {
            onDismiss();
          }

          return result.data;
        }}
      />
    </>
  );
}
